import React, { useState, useEffect } from "react";
import { Container, Typography, Box, Button, IconButton } from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { pb, getCurrentUser, hasAdminPermission } from "../services/pocketbase";
import RankEditor from "../components/RankEditor";
// eslint-disable-next-line
import ReactMarkdown from "react-markdown";

// Discord-style markdown formatting function
const formatDiscordMarkdown = (text) => {
  if (!text) return "";

  // Process complex formats first
  // Underline Bold Italic: __***text***__
  text = text.replace(
    /__\*\*\*(.*?)\*\*\*__/g,
    "<u><strong><em>$1</em></strong></u>"
  );

  // Underline Bold: __**text**__
  text = text.replace(/__\*\*(.*?)\*\*__/g, "<u><strong>$1</strong></u>");

  // Underline Italic: __*text*__
  text = text.replace(/__\*(.*?)\*__/g, "<u><em>$1</em></u>");

  // Bold Italic: ***text***
  text = text.replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>");

  // Then process simpler formats
  // Bold: **text**
  text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Italic: *text* or _text_
  text = text.replace(/\*(.*?)\*|_(.*?)_/g, "<em>$1$2</em>");

  // Underline: __text__
  text = text.replace(/__(.*?)__/g, "<u>$1</u>");

  // Strikethrough: ~~text~~
  text = text.replace(/~~(.*?)~~/g, "<s>$1</s>");

  // Code blocks: ```text```
  text = text.replace(/```(.*?)```/gs, "<pre><code>$1</code></pre>");

  // Inline code: `text`
  text = text.replace(/`(.*?)`/g, "<code>$1</code>");

  // Line breaks
  text = text.replace(/\n/g, "<br />");

  return text;
};

const Ranks = () => {
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedRank, setSelectedRank] = useState(null);
  const user = getCurrentUser();
  const isAdmin = hasAdminPermission(user);

  const fetchRanks = async () => {
    try {
      setLoading(true);
      setError(null);

      const records = await pb.collection("ranks").getList(1, 100, {
        sort: "position",
      });

      setRanks(records.items);
    } catch (err) {
      console.error("Error fetching ranks:", err);
      setError("Failed to load ranks. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRanks();
  }, []);

  const handleAddRank = () => {
    setSelectedRank(null);
    setEditorOpen(true);
  };

  const handleEditRank = (rank) => {
    setSelectedRank(rank);
    setEditorOpen(true);
  };

  // Function to send webhook notification for rank deletion
  const sendDeleteWebhookNotification = async (rankData) => {
    try {
      const timestamp = new Date().toISOString();
      const webhookData = {
        embeds: [
          {
            title: "Rank Deleted",
            color: 0xff0000, // Red color
            fields: [
              {
                name: "Position",
                value: rankData.position.toString(),
                inline: true,
              },
              {
                name: "Title",
                value: rankData.title || "No Title",
                inline: true,
              },
              {
                name: "Deleted By",
                value: user?.username || "Unknown User",
                inline: true,
              },
              {
                name: "Description",
                value: rankData.description || "No Description",
              },
              {
                name: "Timestamp",
                value: new Date().toLocaleString(),
              },
            ],
            timestamp: timestamp,
          },
        ],
      };

      // Send webhook notification
      await fetch("/api/webhooks/rank-logs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });
    } catch (error) {
      console.error("Error sending delete webhook notification:", error);
      // Don't block the main operation if webhook fails
    }
  };

  const handleDeleteRank = async (rankId) => {
    if (!window.confirm("Are you sure you want to delete this rank?")) {
      return;
    }

    try {
      // Get the rank data before deleting
      const rankToDelete = ranks.find(rank => rank.id === rankId);
      
      // Delete the rank
      await pb.collection("ranks").delete(rankId);
      
      // Send webhook notification
      if (rankToDelete) {
        await sendDeleteWebhookNotification(rankToDelete);
      }
      
      await fetchRanks();
    } catch (err) {
      console.error("Error deleting rank:", err);
      setError("Failed to delete rank. Please try again.");
    }
  };

  const handleEditorClose = (refresh) => {
    setEditorOpen(false);
    if (refresh) {
      fetchRanks();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1">
          TOG RANKS
        </Typography>

        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddRank}
            sx={{
              backgroundColor: "#c4d4e0",
              "&:hover": {
                backgroundColor: "#848d94",
              },
            }}
          >
            ADD RANK
          </Button>
        )}
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Typography>Loading ranks...</Typography>
      ) : (
        <Box
          sx={{
            backgroundColor: "rgba(40, 44, 52, 0.99)",
            borderRadius: 2,
          }}
        >
          {ranks.map((rank) => (
            <Box
              key={rank.id}
              sx={{
                position: "relative",
                padding: "20px",
              }}
            >
              <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                {rank.title}
              </Typography>

              <Box
                sx={{
                  whiteSpace: "pre-wrap",
                  "& code": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    padding: "2px 4px",
                    borderRadius: "3px",
                    fontFamily: "monospace",
                  },
                  "& pre": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    padding: "8px",
                    borderRadius: "4px",
                    overflowX: "auto",
                  },
                  "& strong": {
                    fontWeight: "bold",
                  },
                  "& em": {
                    fontStyle: "italic",
                  },
                  "& u": {
                    textDecoration: "underline",
                  },
                  "& s": {
                    textDecoration: "line-through",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: formatDiscordMarkdown(rank.description),
                }}
              />

              {isAdmin && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleEditRank(rank)}
                    sx={{ color: "primary.main" }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteRank(rank.id)}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      <RankEditor
        open={editorOpen}
        onClose={handleEditorClose}
        rank={selectedRank}
      />
    </Container>
  );
};

export default Ranks;
